/**
 * ChapterOptionsSection
 * Sección de opciones del capítulo (cómic, artículos, extras)
 */
import React, { memo, useCallback } from "react";
import { motion } from "framer-motion";
import { Trans } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ComicButton from "../../shared/components/ComicButton/ComicButton";
import ChapterOptionsCoverflow from "./ChapterOptionsCoverflow";
import { extractChapterId } from "../../shared/utils/helpers";

// Componente ChapterOption memoizado
const ChapterOption = memo(({ onClick, imageSrc, imageAlt, labelKey }) => (
    <motion.div
        className="cursor-pointer flex flex-col items-center"
        initial={{ scale: 0.9 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 1 }}
        onClick={onClick}
    >
        <img
            src={imageSrc}
            alt={imageAlt}
            className="w-44 h-44 sm:w-40 sm:h-40 md:w-80 md:h-80 object-contain"
        />
        <h3 className="text-base sm:text-lg md:text-2xl font-semibold text-white">
            <Trans i18nKey={labelKey} />
        </h3>
    </motion.div>
));

ChapterOption.displayName = "ChapterOption";

const ChapterOptionsSection = ({ currentSection, goTo, screenIsSmall }) => {
    const navigate = useNavigate();
    const chapterId = extractChapterId(currentSection);

    // Memoizar callbacks para evitar re-renders de ChapterOption
    const handleComicClick = useCallback(() => goTo(`${chapterId}-comic`), [goTo, chapterId]);
    const handleArticulosClick = useCallback(() => goTo(`${chapterId}-articulos`), [goTo, chapterId]);
    const handleExtrasClick = useCallback(() => goTo(`${chapterId}-extras`), [goTo, chapterId]);
    const handleBack = useCallback(() => navigate(-1), [navigate]);

    // Versión móvil con coverflow
    if (screenIsSmall) {
        return (
            <motion.div
                key={`${currentSection}-opciones`}
                className="absolute inset-0 flex flex-col items-center"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                style={{ height: "calc(var(--vh, 1vh) * 100)" }}
            >
                <div className="mt-4">
                    <h2 className="text-4xl font-bold text-white">
                        <Trans i18nKey="chapterOptions" />
                    </h2>
                </div>
                <div className="flex-1 w-full overflow-hidden" style={{ paddingBottom: "10px" }}>
                    <ChapterOptionsCoverflow
                        goTo={goTo}
                        extractChapterId={extractChapterId}
                        currentSection={currentSection}
                    />
                </div>
                <div className="mb-4">
                    <ComicButton
                        buttonName={<Trans i18nKey="backtoChapters" />}
                        hoverBtnName={<Trans i18nKey="backtoChapters" />}
                        handleButtonClick={handleBack}
                    />
                </div>
            </motion.div>
        );
    }

    // Versión desktop con grid
    return (
        <motion.div
            key={`${currentSection}-opciones`}
            className="absolute inset-0 flex flex-col items-center justify-center text-center"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
        >
            <h2 className="text-4xl font-bold text-white">
                <Trans i18nKey="chapterOptions" />
            </h2>
            <div className="flex flex-col md:flex-row justify-center items-center gap-6">
                <ChapterOption
                    onClick={handleComicClick}
                    imageSrc="/images/comic.webp"
                    imageAlt="Cómic"
                    labelKey="comic"
                />
                <ChapterOption
                    onClick={handleArticulosClick}
                    imageSrc="/images/articulo.webp"
                    imageAlt="Artículos"
                    labelKey="articles"
                />
                <ChapterOption
                    onClick={handleExtrasClick}
                    imageSrc="/images/cofre.webp"
                    imageAlt="Extras"
                    labelKey="extras"
                />
            </div>
            <div className="mt-4">
                <ComicButton
                    buttonName={<Trans i18nKey="backtoChapters" />}
                    hoverBtnName={<Trans i18nKey="backtoChapters" />}
                    handleButtonClick={handleBack}
                />
            </div>
        </motion.div>
    );
};

export default memo(ChapterOptionsSection);
